import { filterVisibleClasses, isClassComplete, resolveClassPanel } from "score-common";
import { compareByClassNumber } from "./classDisplay";

// #3454 -- "who is missing a signature". A judge with no signature on file is
// the show secretary's job to fix (Judge Manager), not the scribe's, so Score
// Admin names them. Pure: the caller hands over what Score Admin already
// loads (the class list, each class's rides with a derived `derivedStatus`,
// and the show's judge roster with `signaturePresent`), nothing is fetched here.
//
// A class counts only when it is still worth signing for:
//   - not hidden (filterVisibleClasses, same posture as every other list),
//   - not paper-scored (`paperEntry === true`: the judge signed the original in
//     ink, a missing e-signature is expected, same rule as scribe-fe's
//     JudgeSignatureWarning),
//   - not complete (every ride FINAL or SCRATCHED, score-common's
//     isClassComplete; a class with no rides yet is not complete).
// Seats are resolved through score-common's resolveClassPanel, the same
// seating score-api and scribe-fe run. A seat whose name matches no roster row
// has no judgeId and is skipped (nothing to fix in Judge Manager for it).
//
// Returns one entry per judge, sorted by name, each carrying the classes they
// sit on (sorted by class number, a class listed once even if the judge holds
// two seats): [{ judgeId, judgeName, classes: [{ id, classNumber, label }] }].
// Empty when every seated judge has a signature (or the roster is unknown).
export function findJudgesMissingSignature({ classes, judges, ridesByClassId }) {
  if (!Array.isArray(judges) || judges.length === 0) return [];
  const signedById = new Map(judges.map((j) => [j.id, j.signaturePresent === true]));
  const byJudge = new Map();

  for (const klass of filterVisibleClasses(classes)) {
    if (klass.paperEntry === true) continue;
    const rides = (ridesByClassId?.[klass.id] ?? []).map((r) => ({
      status: r.derivedStatus ?? r.status ?? null,
    }));
    if (isClassComplete(rides)) continue;

    for (const seat of resolveClassPanel(klass, judges)) {
      if (!seat.judgeId || signedById.get(seat.judgeId) === true) continue;
      const entry = byJudge.get(seat.judgeId) ?? {
        judgeId: seat.judgeId,
        judgeName: seat.judgeName,
        classes: [],
      };
      if (!entry.classes.some((c) => c.id === klass.id)) {
        entry.classes.push({
          id: klass.id,
          classNumber: klass.classNumber ?? null,
          label: klass.classNumber != null && `${klass.classNumber}`.trim() !== "" ? `${klass.classNumber}` : klass.className || klass.id,
        });
      }
      byJudge.set(seat.judgeId, entry);
    }
  }

  return [...byJudge.values()]
    .map((e) => ({ ...e, classes: e.classes.slice().sort(compareByClassNumber) }))
    .sort((a, b) => a.judgeName.localeCompare(b.judgeName));
}
